/**
 * TA Cockpit → Role Prioritisation → **Cluster View**.
 *
 * The cluster leads' own weekly: which cluster to help first, its live capacity what-ifs, and the
 * board where the help is actually cut. Fed by GET /api/ta-cockpit/checkin.
 *
 * ⚠️ WHAT CHANGED ON Sep 25 2026. The priority tier cards, the priority × urgency matrix and the
 * role table LEFT for the Company View — they describe the whole company's open roles, which is the
 * question a business stakeholder opens the cockpit with, not the one this meeting is for. In their
 * place **Balance the load** arrived from the score tab, because re-cutting support between clusters
 * is exactly what the leads are in this view to do.
 *
 * Capacity what-if selections (intern allocation, out-of-office) are stored in THIS browser only
 * (localStorage) — they are meeting scratchpads, not shared state, and never write back to the
 * sheet. ⚠️ The support allocations below them are the same today and should not be: see
 * `BalanceTheLoad`.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { fetchCheckInData } from '../../../services/taCheckinApi';
import type { CapacityState, CheckInData } from '../../../types/taCheckin';
import {
  normaliseCheckInRoles,
  sheetDefaults,
  warehouseAgeHours,
} from '../../../utils/taCheckinIndex';
import { BalanceTheLoad } from '../rolePrioritisation/BalanceTheLoad';
import { ClusterBookedLoad } from './ClusterBookedLoad';
import { ClusterIndexSection } from './ClusterIndexSection';
import { DataHygiene } from './DataHygiene';
import { IndexExplainer } from './IndexExplainer';

const STATE_KEY = 'ta-checkin-capacity-v1';

function loadStoredState(data: CheckInData): CapacityState {
  const def = sheetDefaults(data);
  let stored: Partial<CapacityState> | null = null;
  try {
    stored = JSON.parse(localStorage.getItem(STATE_KEY) || 'null');
  } catch {
    stored = null;
  }
  if (!stored) return def;
  const alloc: Record<string, number> = {};
  const recruitersOut: Record<string, number> = {};
  data.clusters.forEach((c) => {
    alloc[c] = stored?.alloc?.[c] != null ? Number(stored.alloc[c]) : def.alloc[c];
    recruitersOut[c] = Number(stored?.recruitersOut?.[c]) || 0;
  });
  return {
    internCount: stored.internCount != null ? Number(stored.internCount) : def.internCount,
    internsOut: Number(stored.internsOut) || 0,
    alloc,
    recruitersOut,
  };
}

export function CheckInTab({ userEmail }: { userEmail?: string | null }) {
  const [data, setData] = useState<CheckInData | null>(null);
  const [state, setState] = useState<CapacityState | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCheckInData()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setState(loadStoredState(d));
        setError(null);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const roles = useMemo(() => normaliseCheckInRoles(data?.roles ?? []), [data]);

  function persistState(next: CapacityState) {
    setState(next);
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(next));
    } catch {
      // private mode — what-ifs just won't survive a reload
    }
  }

  function resetState() {
    if (data) persistState(sheetDefaults(data));
  }

  async function refresh() {
    setRefreshing(true);
    try {
      const d = await fetchCheckInData(true);
      setData(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  }

  function retry() {
    setLoading(true);
    setError(null);
    fetchCheckInData(true)
      .then((d) => {
        setData(d);
        setState(loadStoredState(d));
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
  }

  /*
   * ⚠️ THE SHEET-FED HALF DEGRADES ON ITS OWN, and Balance the load keeps working underneath it.
   * This used to be one early return for the whole tab, which was right while everything here came
   * from `GET /api/ta-cockpit/checkin` — it no longer is. The support board reads the prioritisation
   * state, not the sheet, so a sheet outage taking it off screen would remove the one thing on this
   * view a lead can still act on. Same rule as the Pipeline Radar's funnel.
   */
  if (loading || error || !data || !state) {
    return (
      <div className="space-y-6">
        {loading ? (
          <div className="flex h-40 items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading check-in data…
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white py-10 text-sm text-rose-700">
            <AlertCircle className="h-5 w-5" />
            <div>Could not load check-in data: {error}</div>
            <div className="text-xs text-gray-500">
              Balance the load below is unaffected — it reads the prioritisation state, not the
              sheet.
            </div>
            <button
              onClick={retry}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 hover:bg-gray-50"
            >
              Retry
            </button>
          </div>
        )}

        {/*
          ⚠️ The booked-load section reads the prioritisation state, not the check-in sheet, so it
          renders through a sheet outage exactly as Balance the load does. Leaving it out of this
          branch would take the tab's own headline off screen for a failure in a different source.
        */}
        <ClusterBookedLoad data={null} />
        <BalanceTheLoad userEmail={userEmail} />
      </div>
    );
  }

  const dataWithRoles: CheckInData = { ...data, roles };
  const generated = new Date(data.generatedAt);
  const matched = roles.filter((r) => r.inSheet).length;
  // The warehouse ETL runs nightly, so say how old the Lever figures are
  // rather than implying Refresh reached Lever itself. Measured against the
  // payload's own timestamp, not the clock, so it stays put while the tab is
  // open and only moves when the data actually does.
  const warehouseAge = (() => {
    const hours = warehouseAgeHours(data);
    if (hours == null) return '';
    return hours < 1 ? ' (just synced)' : ` (synced ${hours}h ago)`;
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">
          Sheet data from{' '}
          {generated.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}{' '}
          · {matched}/{roles.length} open roles matched to the check-in tab by Role ID
          {/*
            ⚠️ THE SHEET'S OWN CAPACITY SPLIT, which used to sit on the deleted intern-pool strip.
            It matters MORE now than it did there, not less: the pool the index is computed against
            is whatever the sheet says and there is no longer a control to change it, so the one
            place that states the figure has to survive the strip that carried it.
          */}
          {data.capacity.ok && (
            <>
              {' '}
              · sheet {data.capacity.month}: {data.capacity.recruitersTotal} recruiters +{' '}
              {data.capacity.internsTotal} interns
            </>
          )}
          {data.interviewLoad?.ok && (
            <> · interviews &amp; pipeline risk live from the warehouse{warehouseAge}</>
          )}
        </span>
        <button
          onClick={refresh}
          disabled={refreshing}
          title="Re-reads the sheet and re-counts interviews and pipeline risk from the warehouse"
          className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/*
        ⚠️ FIRST ON THE TAB (Theresa, Oct 19 2026): "the score is supposed to show the true
        prioritisation distribution first". How much of each cluster's week the open roles have
        booked is the question the leads arrive with, and it was a tab away in the Company View's
        collapsed §03. The index below keeps everything it had — role load, booked interviews,
        pipeline risk — it simply stops being the first thing read.
      */}
      <ClusterBookedLoad data={dataWithRoles} />

      <ClusterIndexSection
        data={dataWithRoles}
        state={state}
        onStateChange={persistState}
        onReset={resetState}
      />

      <BalanceTheLoad userEmail={userEmail} />

      <DataHygiene data={dataWithRoles} />

      {/* Reference material, at the bottom and shut — see `IndexExplainer`. */}
      <IndexExplainer data={dataWithRoles} state={state} />
    </div>
  );
}
